import { useState } from 'react';

import { BankAccountTypeSchema } from '@dcc-erp/shared';
import type { BankAccountType, CreateVendorBankChange } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useCreateVendorBankChange, useSubmitVendorBankChange } from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';

interface Props {
  companyId: string | undefined;
  supplierId: string;
  vendorId: string;
  onClose: () => void;
}

const labelClass = 'meta';
const labelStyle = { display: 'block', marginBottom: 6 } as const;
const BANK_TYPES = BankAccountTypeSchema.options;

// VendorBankChangeModal — capture the PROPOSED new* bank for a Vendor branch and
// open a VBC 4-eyes request (D2). On save we create the DRAFT request then submit
// it for approval (DRAFT → submit), so the maker hands off a PENDING request to
// the approver pool. The actual Vendor bank columns are written only on final
// approval (D1) — never here.
export function VendorBankChangeModal({ companyId, supplierId, vendorId, onClose }: Props) {
  const createMut = useCreateVendorBankChange(companyId, supplierId, vendorId);
  const submitMut = useSubmitVendorBankChange(companyId, supplierId, vendorId);

  const [accountNumber, setAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankBranchCode, setBankBranchCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountType, setAccountType] = useState<BankAccountType | ''>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy = createMut.isPending || submitMut.isPending;
  const canSave = accountNumber.trim().length > 0 && bankCode.trim().length > 0;

  async function handleSave() {
    setError(null);
    // Only send the fields the maker actually filled — a partial change inherits
    // the rest from the Vendor snapshot server-side.
    const body: CreateVendorBankChange = {
      ...(accountNumber.trim() ? { newBankAccountNumber: accountNumber.trim() } : {}),
      ...(bankCode.trim() ? { newBankCode: bankCode.trim() } : {}),
      ...(bankBranchCode.trim() ? { newBankBranchCode: bankBranchCode.trim() } : {}),
      ...(accountHolderName.trim() ? { newAccountHolderName: accountHolderName.trim() } : {}),
      ...(accountType ? { newBankAccountType: accountType } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
    try {
      const created = await createMut.mutateAsync(body);
      await submitMut.mutateAsync(created.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <AppModal
      title="ขอเปลี่ยนบัญชีธนาคาร"
      subtitle="คำขอจะถูกส่งให้ผู้อนุมัติ (4-eyes) — บัญชีจะเปลี่ยนเมื่ออนุมัติแล้วเท่านั้น"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="vbc-save"
            disabled={busy || !canSave}
            onClick={() => void handleSave()}
          >
            <Icon name="check" size={14} /> {busy ? 'กำลังส่ง…' : 'ส่งคำขออนุมัติ'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <label className={labelClass} style={labelStyle}>
            เลขบัญชีใหม่ *
          </label>
          <input
            className="input"
            data-testid="vbc-account"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            ธนาคารใหม่ *
          </label>
          <input
            className="input"
            data-testid="vbc-code"
            value={bankCode}
            onChange={(e) => setBankCode(e.target.value)}
            placeholder="KBANK / BBL / SCB"
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            รหัสสาขาธนาคาร
          </label>
          <input
            className="input"
            data-testid="vbc-branch"
            value={bankBranchCode}
            onChange={(e) => setBankBranchCode(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            ประเภทบัญชี
          </label>
          <select
            className="input"
            data-testid="vbc-type"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as BankAccountType | '')}
            style={{ width: '100%' }}
          >
            <option value="">—</option>
            {BANK_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className={labelClass} style={labelStyle}>
            ชื่อบัญชี
          </label>
          <input
            className="input"
            data-testid="vbc-holder"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label className={labelClass} style={labelStyle}>
            เหตุผล (ถ้ามี)
          </label>
          <textarea
            className="input"
            data-testid="vbc-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>
      </div>
      {error && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 12 }}
          data-testid="vbc-modal-error"
        >
          {error}
        </div>
      )}
    </AppModal>
  );
}
